import React, {
  useState, useCallback, useMemo, useEffect, useContext,
} from 'react';
import { Drawer, Row } from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';
import { updateData, locateWorkflowParentElement } from './workflowHelpers';
import FormWorkflowContext from './FormWorkflowContext';
import PDFExportUserSelector from './selectors/PDFExportUserSelector'; // Add this import

import WorkflowActionNode from './WorkflowActionNode';
import WorkflowHandle from './WorkflowHandle';
import FormExport from '../FormExport/FormExport';
import { AdditionalItem, AdditionalLabel } from './AssignmentDropdownItems';

import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import BorderlessButton from '../../common/buttons/BorderlessButton';

const HOVER_TEXT = `
Use this step to save a PDF copy of completed forms to your
preferred destination.
`;

const ERROR_DETAILS = {
  content: (
    <div style={{ maxWidth: 300 }}>
      This step requires the <b>Export Location</b>, <b>Use Form Project Folder</b>, or <b>Save to User Files Tab</b> field to be configured
      If <b>Save to User Files Tab</b> is enabled, a user must also be selected.
    </div>
  ),
};

export default ({
  onNodeUpdate,
  sections = [],
  collected = {},
  drawOptions = [],
  fileMap = {},
  formRef,
  onPDFChange,
  setDataMap,
  isDisplay,
  useStandardTemplate,
  setDisableNext,
  divisionId,
  divisionIds = [],
  isExternalForm,
} = {}) => function ({ id, draggable, data = {} }) {
  const {
    dataMap = {},
    elements = [],
  } = useContext(FormWorkflowContext) ?? {};

  // Merge data prop with dataMap[id]
  const nodeData = useMemo(() => ({
    ...data,
    ...(dataMap[id] || {}),
  }), [data, dataMap, id]);
  const {
    exportLocation: initialExportLocation,
    useFormProjects: initialUseFormProjects,
    projectSubfolder: initialProjectSubfolder,
    saveToUserFiles,
    userToSaveTo: initialUserToSaveTo,
  } = nodeData;
  const [exportLocation, setExportLocation] = useState(initialExportLocation);
  const [useFormProjects, setUseFormProjects] = useState(initialUseFormProjects);
  const [projectSubfolder, setProjectSubfolder] = useState(initialProjectSubfolder);
  const [userToSaveTo, setUserToSaveTo] = useState(initialUserToSaveTo);
  const [showPDF, setShowPDF] = useState(false);
  const [isProjectSubfolderInvalid, setIsProjectSubfolderInvalid] = useState(false);
  const openPreview = useCallback(() => setShowPDF(true), []);
  const hidePreview = useCallback(() => setShowPDF(false), []);

  const createUserOptions = useMemo(() => {
    // Find the upstream createUser node if it exists
    const upstreamCreateUser = locateWorkflowParentElement(id, 'createUser', elements);
    return upstreamCreateUser ? [{
      id: `createUser-${upstreamCreateUser.id}`,
      children: (
        <AdditionalItem
          title="Created User"
          help="The user created by this Create User workflow step"
        />
      ),
      label: (
        <AdditionalLabel
          title="Created User"
          help="The user created by this Create User workflow step"
        />
      ),
    }] : [];
  }, [elements, id]);

  const handleSaveToUserFilesChange = useCallback((checked) => {
    const update = { saveToUserFiles: checked };
    if (!checked) {
      setUserToSaveTo(null);
      update.userToSaveTo = null;
    }
    setDataMap(updateData(id, update));
  }, [setDataMap, id]);

  // Handle user selection from PDFExportUserSelector
  const handleUserSelect = useCallback((selectedUser) => {
    setUserToSaveTo(selectedUser);
    setDataMap(updateData(id, { userToSaveTo: selectedUser }));
  }, [setDataMap, id]);

  const savePreview = useCallback(() => {
    if (formRef && formRef.current) {
      const {
        exportLocation: newExportLocation,
        useFormProjects: newUseFormProjects,
        projectSubfolder: newProjectSubfolder,
        userToSaveTo: newUserToSaveTo,
        saveToUserFiles: newSaveToUserFiles,
      } = formRef.current.getFieldsValue();
      const update = {};
      if (newExportLocation) {
        setExportLocation(newExportLocation);
        update.exportLocation = newExportLocation;
      }
      if (newUseFormProjects) {
        setUseFormProjects(newUseFormProjects);
        update.useFormProjects = newUseFormProjects;
      }
      if (newProjectSubfolder) {
        const isValidSubfolder = newProjectSubfolder.slice(-1) === '/';
        const subfolderPath = isValidSubfolder ? newProjectSubfolder : `${newProjectSubfolder}/`;
        setProjectSubfolder(isValidSubfolder ? newProjectSubfolder : `${newProjectSubfolder}/`);
        update.projectSubfolder = subfolderPath;
      }
      if (newUserToSaveTo) {
        setUserToSaveTo(newUserToSaveTo);
        update.userToSaveTo = newUserToSaveTo;
      }
      if (newSaveToUserFiles) {
        update.saveToUserFiles = newSaveToUserFiles;
      }
      setDataMap(updateData(id, update));
      setShowPDF(false);
    }
  }, [formRef, setDataMap, id]);

  const isDisabled = (!useFormProjects && !exportLocation)
    || (saveToUserFiles && !userToSaveTo);

  useEffect(() => {
    if (!setDisableNext) return;
    setDisableNext((prev) => ({
      ...prev,
      [id]: isDisabled,
    }));
  }, [isDisabled, setDisableNext, id]);

  // Update title to show only export location (remove user info)
  const title = useMemo(() => (
    useFormProjects
      ? `{Project}/${projectSubfolder || ''}`
      : exportLocation || 'Set'
  ), [useFormProjects, exportLocation, projectSubfolder]);

  return (
    // eslint-disable-next-line react/jsx-filename-extension
    <div>
      <WorkflowActionNode
        title="PDF Export"
        Icon={FilePdfOutlined}
        type="pdf"
        id={id}
        draggable={draggable}
        onNodeUpdate={(...args) => {
          onNodeUpdate(...args);
          if (setDisableNext) {
            setDisableNext((prev) => ({ ...prev, [id]: false }));
          }
        }}
        onPreview={openPreview}
        isDisplay={isDisplay}
        hover={HOVER_TEXT}
        errorPopover={isDisabled ? ERROR_DETAILS : null}
      >
        {!draggable && (
          <div>
            <Row style={{ marginTop: 5 }}>
              Select Export Location:
            </Row>
            <BorderlessButton
              title={title}
              onClick={openPreview}
              style={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap', textOverflow: 'ellipsis' }}
            />
            <Row style={{ marginTop: 10 }}>
              <OnTraccrCheckbox
                value={saveToUserFiles}
                onChange={handleSaveToUserFilesChange}
                label="Save to User Profile"
              />
            </Row>
            {saveToUserFiles && (
              <div style={{ marginTop: 10, width: '100%' }}>
                <Row style={{ marginBottom: 5 }}>
                  Select User:
                </Row>
                <Row style={{ width: '100%', display: 'block' }}>
                  <PDFExportUserSelector
                    sections={sections}
                    initialSelected={userToSaveTo}
                    divisionId={divisionId}
                    divisionIds={divisionIds}
                    onChange={handleUserSelect}
                    showFormAuthor={!isExternalForm}
                    isDisplay={isDisplay}
                    additionalOptions={createUserOptions}
                  />
                </Row>
              </div>
            )}
            <WorkflowHandle id={`${id}-target`} type="target" position="top" disabled={isDisplay} />
          </div>
        )}
      </WorkflowActionNode>
      <Drawer
        title="PDF Export"
        width={700}
        visible={showPDF}
        onClose={hidePreview}
      >
        <FormExport
          formRef={formRef}
          sections={sections}
          collected={collected}
          drawOptions={drawOptions}
          onPDFChange={onPDFChange}
          exportLocation={exportLocation}
          useFormProjects={useFormProjects}
          projectSubfolder={projectSubfolder}
          fileMap={fileMap}
          useStandardTemplate={useStandardTemplate}
          onProjectSubfolderInvalidChange={setIsProjectSubfolderInvalid}
        />
        <div className="drawer-footer">
          <Row justify="end" gutter={10}>
            <OnTraccrButton
              title="Cancel"
              type="cancel"
              style={{ marginRight: 8 }}
              onClick={hidePreview}
            />
            <OnTraccrButton
              title="Save"
              onClick={savePreview}
              disabled={isProjectSubfolderInvalid}
            />
          </Row>
        </div>
      </Drawer>
    </div>
  );
};
